import { useLingui } from "@lingui/react";
import type { CarAd } from "@mobility-item-page/types";
import { formatNumber } from "@mobility-item-page/utilities";
import { useAppConfig } from "src/context/AppConfigProviderContext.tsx";
import FieldValuePair from "../FieldValuePair.tsx";
import { DesktopBatteryCapacity, MobileBatteryCapacity } from "./BatteryCapacity.tsx";
import { ContactAction, ContactTopic } from "./ContactAction.tsx";
import { EvExpandableRow } from "./EvExpandableRow.tsx";
import { EvFaqLinks } from "./EvFaqLinks.tsx";
import { EvInfoCard } from "./EvInfoCard.tsx";

type Props = {
  ad: CarAd;
};

/**
 * Range and battery card (MBJ-300) — one of the cards inside the EV info section.
 */
export const RangeAndBatteryCard = ({ ad }: Props) => {
  const { i18n } = useLingui();
  const { locale } = useAppConfig();

  const drivingRange = ad.driving_range;
  const consumption = ad.fuel_consumption?.combined_kilowatt_hours;
  const rangePrKwh = ad.range_pr_kwh;

  return (
    <EvInfoCard
      testId="ev-range-and-battery"
      title={i18n.t({
        id: "itemPageLayout.ev.rangeAndBattery.title",
        message: "Range and battery",
        comment: "Heading of the range and battery card on electric car ads",
      })}
    >
      <dl className="m-0 lg:grid lg:grid-cols-2 lg:gap-x-32">
        <FieldValuePair
          field={i18n.t({ id: "itemPageLayout.keyInfo.driving_range" })}
          value={
            drivingRange != null
              ? i18n.t({
                  id: "itemPageLayout.common.distance.kilometer.postfix",
                  values: { distance: formatNumber(drivingRange, locale) },
                })
              : i18n.t({ id: "itemPageLayout.sectionSummary.notSpecified" })
          }
          tooltip={i18n.t({
            id: "itemPageLayout.keyInfo.tooltip.wltp",
          })}
        />

        <DesktopBatteryCapacity batteryCapacity={ad.battery_capacity} />
      </dl>

      <div className="border-t">
        <MobileBatteryCapacity batteryCapacity={ad.battery_capacity} />

        {consumption ? (
          <EvExpandableRow
            title={i18n.t({
              id: "itemPageLayout.ev.rangeAndBattery.consumption.title",
              message: "Consumption per 100 km",
              comment: "Title of the energy consumption expandable on electric car ads",
            })}
            value={i18n.t({
              id: "itemPageLayout.ev.rangeAndBattery.consumption.value",
              message: "{consumption} kWh",
              comment:
                "Energy consumption value with its unit, on the range and battery card. Per 100 km is already stated in the title",
              values: { consumption: formatNumber(consumption, locale) },
            })}
            body={i18n.t({
              id: "itemPageLayout.ev.rangeAndBattery.consumption.body",
              message:
                "How much electricity the car uses per 100 km (like liters per 100 km, but in kWh) — lower means cheaper to drive. Actual consumption will deviate based on the car's age, mileage, driving patterns, and weather.",
              comment: "Body text of the energy consumption expandable on electric car ads",
            })}
          />
        ) : null}

        {rangePrKwh ? (
          <EvExpandableRow
            title={i18n.t({
              id: "itemPageLayout.ev.rangeAndBattery.rangePerKwh.title",
              message: "Range per kWh",
              comment: "Title of the range per kWh expandable on electric car ads",
            })}
            // The generic distance postfix
            value={i18n.t({
              id: "itemPageLayout.common.distance.kilometer.postfix",
              values: { distance: formatNumber(rangePrKwh, locale) },
            })}
            body={i18n.t({
              id: "itemPageLayout.ev.rangeAndBattery.rangePerKwh.body",
              message:
                "How far the car goes per kWh (like km per liter, but electric) — higher means cheaper to drive. Actual efficiency will deviate based on the car's age, mileage, driving patterns, and weather.",
              comment: "Body text of the range per kWh expandable on electric car ads",
            })}
          />
        ) : null}

        <EvExpandableRow
          title={i18n.t({
            id: "itemPageLayout.ev.rangeAndBattery.batteryHealth.title",
            message: "Battery health",
            comment: "Title of the battery health expandable on electric car ads",
          })}
          body={i18n.t({
            id: "itemPageLayout.ev.rangeAndBattery.batteryHealth.body",
            message:
              "A State-of-Health (SoH) test shows how much of the battery's original capacity remains — which directly determines the car's real-world range today. A score of 85–95% is typical after a few years, but it varies based on age, mileage, and charging habits.",
            comment: "Body text of the battery health expandable on electric car ads",
          })}
          action={<ContactAction topic={ContactTopic.BatteryHealth} />}
        />

        <EvExpandableRow
          title={i18n.t({
            id: "itemPageLayout.ev.rangeAndBattery.batteryWarranty.title",
            message: "Battery warranty",
            comment: "Title of the battery warranty expandable on electric car ads",
          })}
          body={i18n.t({
            id: "itemPageLayout.ev.rangeAndBattery.batteryWarranty.body",
            message:
              "Batteries on electric cars usually have a longer warranty than the car itself. Most manufacturers provide a separate battery warranty, often about 8 years or 160,000 km (whichever comes first) from first registration. This guarantees a minimum capacity (usually ~70%) and covers manufacturing faults or severe degradation. Coverage almost always transfers to the new owner.",
            comment: "Body text of the battery warranty expandable on electric car ads",
          })}
          action={<ContactAction topic={ContactTopic.BatteryWarranty} />}
        />
      </div>

      <EvFaqLinks links={ad.ev_info_links?.battery_and_range} />
    </EvInfoCard>
  );
};
